/**
 * @file registerApi.ts
 * @brief Web sign-up calls: register a user or a company, the live
 *        "already taken" checks, and the post-signup answers.
 *
 * @details
 * Same public routes the phone uses (FRONTEND/src/features/auth/registerApi.ts,
 * onboardingApi.ts); nothing new on the server. The two checks return `null`
 * for "unknown" (offline, rate-limited) so the form stays quiet instead of
 * blocking; `/register*` still answers 409 for a taken email or name.
 *
 * @references
 * - BACKEND/src/features/auth/routes.js (register, register-company,
 *   check-email, onboarding)
 * - BACKEND/src/features/company/routes.js (check-name, public)
 *
 * @author DES
 * @date 01-10-2026
 */

import { apiClient, apiGet, apiPost } from "@/shared/api/client";
import { authEndpoints, companyEndpoints } from "@/shared/api/endpoints";

export type RegisterUserInput = {
  title: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  nativeLanguage: string;
  password: string;
};

export type RegisterCompanyInput = {
  title: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  password: string;
  companyName: string;
  businessDomain?: string;
};

type RegisterResponse = { token: string };

/**
 * The browser's region ("GB", "PK") as the phone sends its device region:
 * it picks the account's billing currency server-side. Best-effort; the
 * server falls back to its default currency without it.
 */
function browserRegionCode(): string | undefined {
  try {
    const region = new Intl.Locale(navigator.language).maximize().region;
    return region ? region.toUpperCase().slice(0, 2) : undefined;
  } catch {
    return undefined;
  }
}

export function registerUser(input: RegisterUserInput): Promise<RegisterResponse> {
  return apiPost<RegisterResponse>(authEndpoints.register, {
    ...input,
    country: browserRegionCode(),
  });
}

export function registerCompany(input: RegisterCompanyInput): Promise<RegisterResponse> {
  // The admin's country too (night E2E F1, 21-10-2026): billing reads it.
  return apiPost<RegisterResponse>(authEndpoints.registerCompany, {
    ...input,
    country: browserRegionCode(),
  });
}

/** `true` free, `false` taken, `null` unknown. */
export async function checkEmailAvailable(email: string): Promise<boolean | null> {
  try {
    const data = await apiPost<{ available?: boolean }>(authEndpoints.checkEmail, { email });
    return typeof data?.available === "boolean" ? data.available : null;
  } catch {
    return null;
  }
}

/** Same as the email check, plus the server's reason ("reserved"). */
export async function checkCompanyName(
  name: string,
): Promise<{ available: boolean; message?: string } | null> {
  try {
    const data = await apiPost<{ available?: boolean; message?: string }>(
      companyEndpoints.checkName,
      { name },
    );
    if (typeof data?.available !== "boolean") return null;
    return { available: data.available, message: data.message };
  } catch {
    return null;
  }
}

export type OnboardingAnswer = {
  source?: string;
  detail?: string;
  primaryUse?: string;
};

/**
 * Files a post-signup answer with the token `/register` returned. The user
 * has not signed in yet, so the token goes on this one request only and is
 * never stored. Best-effort, as on the phone: a failure is a skip.
 */
export async function submitOnboarding(token: string, answer: OnboardingAnswer): Promise<void> {
  try {
    await apiClient.post(authEndpoints.onboarding, answer, {
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch {
    /* skipped */
  }
}

/**
 * Record that a new account agreed to the current Terms + Privacy
 * (GDPR, 06-10-2026). Best-effort: if it fails, TermsGate asks after login.
 */
export async function acceptCurrentTerms(token: string): Promise<void> {
  try {
    const info = await apiGet<{ version?: string }>(authEndpoints.termsInfo);
    if (!info?.version) return;
    await apiClient.post(
      authEndpoints.acceptTerms,
      { version: info.version },
      { headers: { Authorization: `Bearer ${token}` } },
    );
  } catch {
    /* asked again after login */
  }
}
